import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import ProductCard from '../../components/user/ProductCard';
import { resolveMedia, useSettings } from '../../context/SettingsContext';
import { defaultHomeBanner, defaultHomeBanners, mergeHomeContent } from '../../data/homeContent';

function Icon({ name, className = 'h-5 w-5' }) {
  const paths = {
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>,
    leaf: <><path d="M20 4c-8 0-14 4-14 10 0 3 2 5 5 5 6 0 9-7 9-15Z"/><path d="M4 21c2-6 6-10 12-13"/></>,
    hand: <><path d="M7 11V6a2 2 0 0 1 4 0v4-6a2 2 0 0 1 4 0v6-4a2 2 0 0 1 4 0v8c0 5-3 8-8 8-3 0-5-1-7-4l-2-3a2 2 0 0 1 3-2l2 2"/></>,
    truck: <><path d="M1 4h14v13H1zM15 9h4l4 4v4h-8z"/><circle cx="6" cy="19" r="2"/><circle cx="18" cy="19" r="2"/></>,
    check: <path d="m5 12 4 4L19 6"/>,
    arrow: <path d="M5 12h14m-5-5 5 5-5 5"/>,
  };
  return <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}

export default function Home() {
  const { settings } = useSettings();
  const [bestSellers, setBestSellers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const home = mergeHomeContent(settings?.homeContent);
  const banners = settings?.banners && settings.banners.length > 0 ? settings.banners : defaultHomeBanners;

  useEffect(() => {
    api.get('/products?limit=8').then(({ data }) => {
      const sorted = [...(data.items || [])].sort((a, b) => Number(b.isBestSeller) - Number(a.isBestSeller));
      setBestSellers(sorted.slice(0, 4));
    }).catch(() => {});

    api.get('/blogs').then(({ data }) => {
      setArticles((data.items || []).slice(0, 3));
    }).catch(() => {});
  }, []);

  // Automatic slide rotation
  useEffect(() => {
    if (banners.length < 2 || isPaused) return undefined;
    const intervalId = window.setInterval(() => {
      setActiveBannerIndex((index) => (index + 1) % banners.length);
    }, 5000);
    return () => window.clearInterval(intervalId);
  }, [banners.length, isPaused]);

  const handlePrevSlide = () => {
    setActiveBannerIndex((index) => (index === 0 ? banners.length - 1 : index - 1));
  };

  const handleNextSlide = () => {
    setActiveBannerIndex((index) => (index + 1) % banners.length);
  };

  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    if (diff > 40) {
      handleNextSlide();
    } else if (diff < -40) {
      handlePrevSlide();
    }
    setTouchStartX(null);
  };

  const activeBanner = banners[activeBannerIndex] || banners[0] || defaultHomeBanner;

  return (
    <div className="bg-white text-[#241914]">
      {/* Luxury Hero Carousel Section */}
      <section
        className="relative h-[500px] md:h-[560px] lg:h-[620px] flex items-center overflow-hidden border-b border-[#3D251A] group select-none"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Background Image Slides with Smooth Cross-Fade */}
        {banners.map((banner, index) => {
          const isActive = index === activeBannerIndex;
          return (
            <div
              key={banner._id || index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                isActive ? 'opacity-100 z-0' : 'opacity-0 z-0 pointer-events-none'
              }`}
            >
              <img
                src={resolveMedia(banner.image)}
                alt={banner.title || 'Chandana Aura sandalwood collection'}
                className={`w-full h-full object-cover object-right md:object-center filter brightness-[0.9] contrast-[1.05] transition-transform duration-[7000ms] ease-out ${
                  isActive ? 'scale-100' : 'scale-105'
                }`}
                onError={(e) => {
                  e.target.src = '/assets/hero_slide_ganesha.jpg';
                }}
              />
            </div>
          );
        })}

        {/* High-Contrast Foreground Text Layer */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full py-16 pointer-events-auto">
          <div className="max-w-xl space-y-6">
            <h1
              key={`title-${activeBannerIndex}`}
              className="font-hero text-4xl sm:text-5xl lg:text-6xl font-normal leading-[1.1] tracking-tight text-[#FAF5EE] drop-shadow-xl animate-fadeIn"
            >
              {activeBanner.title}
            </h1>

            <p
              key={`sub-${activeBannerIndex}`}
              className="text-sm sm:text-base text-[#E0D2C3] font-normal leading-relaxed max-w-md drop-shadow-lg animate-fadeIn"
            >
              {activeBanner.subtitle}
            </p>

            <div className="pt-2">
              <Link
                to={activeBanner.link || '/shop'}
                className="inline-block bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-widest px-8 py-3.5 rounded-sm shadow-2xl transition-all hover:scale-105"
              >
                {activeBanner.buttonText || 'Shop now'}
              </Link>
            </div>
          </div>
        </div>

        {/* Carousel Prev/Next Buttons */}
        {banners.length > 1 && (
          <>
            <button
              type="button"
              onClick={handlePrevSlide}
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/40 hover:bg-[#C49A45] text-white flex items-center justify-center backdrop-blur-sm border border-white/20 opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-xl cursor-pointer"
              aria-label="Previous Slide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <button
              type="button"
              onClick={handleNextSlide}
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/40 hover:bg-[#C49A45] text-white flex items-center justify-center backdrop-blur-sm border border-white/20 opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-xl cursor-pointer"
              aria-label="Next Slide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>

            {/* Slide Indicator Bars */}
            <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2.5 items-center bg-black/30 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/10">
              {banners.map((banner, index) => (
                <button
                  type="button"
                  key={banner._id || index}
                  onClick={() => setActiveBannerIndex(index)}
                  className={`h-2 rounded-full transition-all duration-500 cursor-pointer ${
                    index === activeBannerIndex ? 'w-8 bg-[#D4AF37]' : 'w-2.5 bg-white/40 hover:bg-white/70'
                  }`}
                  aria-label={`Show banner ${index + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {home.trust.isActive && <section className="border-b border-[#E8E1DB] bg-[#F7F5F2]">
        <div className="mx-auto grid max-w-7xl grid-cols-2 px-4 sm:px-6 lg:grid-cols-4">
          {(home.trust.items || []).map((item, index) => <div key={`${item.title}-${index}`} className={`flex min-h-[82px] items-center gap-3 px-2 py-4 sm:px-5 ${index % 2 === 0 ? 'border-r border-[#E1D8D0]' : ''} lg:border-r lg:last:border-r-0`}><Icon name={['shield', 'leaf', 'hand', 'truck'][index % 4]} className="h-5 w-5 shrink-0 text-[#9D772D]"/><div><p className="text-[11px] font-bold text-[#241914] sm:text-xs">{item.title}</p><p className="mt-0.5 text-[9px] text-[#76675E] sm:text-[10px]">{item.detail}</p></div></div>)}
        </div>
      </section>}

      {home.collections.isActive && <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="mb-7 flex items-end justify-between gap-4">
          <div><p className="text-[10px] font-bold uppercase text-[#9D772D]">{home.collections.eyebrow}</p><h2 className="mt-1 font-heading text-[30px] font-semibold leading-tight sm:text-[38px]">{home.collections.title}</h2></div>
          <Link to="/shop" className="hidden items-center gap-2 text-[11px] font-bold text-[#7F5D1E] sm:flex">View all <Icon name="arrow" className="h-4 w-4"/></Link>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 lg:grid-rows-2">
          {(home.collections.items || []).map((category, index) => <Link key={`${category.name}-${index}`} to={category.link || '/shop'} className={`group relative min-h-[210px] overflow-hidden rounded-md bg-[#EEEAE6] sm:min-h-[250px] ${category.featured ? 'col-span-2 lg:row-span-2 lg:min-h-[516px]' : ''}`}>
            <img src={resolveMedia(category.image)} alt={category.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.035]"/>
            <div className="absolute inset-x-0 bottom-0 bg-white/95 p-3.5 sm:p-4">
              <div className="flex items-center justify-between gap-3"><div className="min-w-0"><h3 className="font-heading text-lg font-semibold leading-tight sm:text-xl">{category.name}</h3><p className="mt-1 truncate text-[9px] text-[#76675E] sm:text-[10px]">{category.detail}</p></div><span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[#D9D0C8] text-[#9D772D] transition group-hover:bg-[#241914] group-hover:text-white"><Icon name="arrow" className="h-3.5 w-3.5"/></span></div>
            </div>
          </Link>)}
        </div>
        <Link to="/shop" className="mt-5 flex items-center justify-center gap-2 border-b border-[#D9D0C8] pb-3 text-[11px] font-bold text-[#7F5D1E] sm:hidden">View all products <Icon name="arrow" className="h-4 w-4"/></Link>
      </section>}

      {home.favourites.isActive && <section className="border-y border-[#E8E1DB] bg-[#F7F5F2]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
          <div className="mb-7 flex items-end justify-between gap-4"><div><p className="text-[10px] font-bold uppercase text-[#9D772D]">{home.favourites.eyebrow}</p><h2 className="mt-1 font-heading text-[30px] font-semibold leading-tight sm:text-[38px]">{home.favourites.title}</h2></div><Link to={home.favourites.link || '/shop'} className="hidden items-center gap-2 text-[11px] font-bold text-[#7F5D1E] sm:flex">{home.favourites.linkText} <Icon name="arrow" className="h-4 w-4"/></Link></div>
          {bestSellers.length === 0 ? <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-5">{Array.from({ length: Number(home.favourites.limit) || 4 }).map((_, index) => <div key={index} className="animate-pulse"><div className="aspect-[4/5] rounded bg-[#E5E0DB]"/><div className="mt-3 h-3 w-4/5 rounded bg-[#E5E0DB]"/></div>)}</div> : <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">{bestSellers.slice(0, Number(home.favourites.limit) || 4).map((product) => <ProductCard key={product._id} product={product}/>)}</div>}
        </div>
      </section>}

      {home.story.isActive && <section className="grid border-b border-[#E8E1DB] lg:grid-cols-2">
        <div className="min-h-[340px] bg-[#EEEAE6] sm:min-h-[480px] lg:order-2"><img src={resolveMedia(home.story.image)} alt={home.story.title} className="h-full w-full object-cover" onError={(event) => { event.currentTarget.src = '/assets/sandalwood_agarbatti.jpg'; }}/></div>
        <div className="flex items-center bg-white px-5 py-12 sm:px-10 sm:py-16 lg:order-1 lg:px-[max(3rem,calc((100vw-80rem)/2))]">
          <div className="max-w-xl"><p className="text-[10px] font-bold uppercase text-[#9D772D]">{home.story.eyebrow}</p><h2 className="mt-2 font-heading text-[32px] font-semibold leading-[1.08] sm:text-[44px]">{home.story.title}</h2><p className="mt-5 text-sm leading-7 text-[#665950]">{home.story.description}</p><ul className="mt-6 grid gap-3 text-xs text-[#4C413B] sm:grid-cols-2">{(home.story.bullets || []).map((item) => <li key={item} className="flex items-center gap-2"><span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-[#EEF4EE] text-[#34704A]"><Icon name="check" className="h-3 w-3"/></span>{item}</li>)}</ul><div className="mt-8 flex flex-wrap gap-3"><Link to={home.story.primaryLink || '/authenticity'} className="rounded bg-[#241914] px-5 py-3 text-[10px] font-bold uppercase text-white">{home.story.primaryText}</Link><Link to={home.story.secondaryLink || '/about'} className="rounded border border-[#9D772D] px-5 py-3 text-[10px] font-bold uppercase text-[#7F5D1E]">{home.story.secondaryText}</Link></div></div>
        </div>
      </section>}

      {home.standard.isActive && <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        <div className="grid gap-8 md:grid-cols-[.75fr_1.25fr] md:gap-14"><div><p className="text-[10px] font-bold uppercase text-[#9D772D]">{home.standard.eyebrow}</p><h2 className="mt-1 font-heading text-[30px] font-semibold leading-tight sm:text-[38px]">{home.standard.title}</h2></div><div className="grid gap-7 sm:grid-cols-3">{(home.standard.items || []).map((item) => <div key={item.number} className="border-t border-[#CFC5BD] pt-4"><span className="text-[10px] font-bold text-[#9D772D]">{item.number}</span><h3 className="mt-3 font-heading text-xl font-semibold">{item.title}</h3><p className="mt-2 text-[11px] leading-5 text-[#76675E]">{item.detail}</p></div>)}</div></div>
      </section>}

      {home.journal.isActive && articles.length > 0 && <section className="border-y border-[#E8E1DB] bg-[#F7F5F2]">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
          <div className="mb-7 flex items-end justify-between gap-4">
            <div><p className="text-[10px] font-bold uppercase text-[#9D772D]">{home.journal.eyebrow}</p><h2 className="mt-1 font-heading text-[30px] font-semibold leading-tight sm:text-[38px]">{home.journal.title}</h2></div>
            <Link to="/blog" className="hidden items-center gap-2 text-[11px] font-bold text-[#7F5D1E] sm:flex">{home.journal.linkText} <Icon name="arrow" className="h-4 w-4"/></Link>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {articles.map((article) => <Link key={article._id} to={`/blog/${article.slug}`} className="group grid grid-cols-[112px_1fr] overflow-hidden border-t border-[#CFC5BD] pt-4 md:block">
              <div className="aspect-[4/3] overflow-hidden rounded bg-[#E8E2DC] md:aspect-[16/9]"><img src={article.image || '/assets/purity_banner.jpg'} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.035]" onError={(event) => { event.currentTarget.src = '/assets/purity_banner.jpg'; }}/></div>
              <div className="min-w-0 pl-4 md:pl-0 md:pt-4"><p className="text-[9px] font-bold uppercase text-[#9D772D]">{article.category || 'Sandalwood guide'}</p><h3 className="mt-1 font-heading text-lg font-semibold leading-tight group-hover:text-[#7F5D1E]">{article.title}</h3><p className="mt-2 hidden text-[11px] leading-5 text-[#76675E] md:line-clamp-2">{article.excerpt}</p><span className="mt-3 inline-flex items-center gap-1.5 text-[9px] font-bold uppercase text-[#7F5D1E]">Read article <Icon name="arrow" className="h-3.5 w-3.5"/></span></div>
            </Link>)}
          </div>
          <Link to="/blog" className="mt-6 flex items-center justify-center gap-2 border-t border-[#D9D0C8] pt-4 text-[11px] font-bold text-[#7F5D1E] sm:hidden">View all articles <Icon name="arrow" className="h-4 w-4"/></Link>
        </div>
      </section>}

      {home.guidance.isActive && <section className="border-b border-[#E8E1DB] bg-white">
        <div className="mx-auto grid max-w-7xl px-4 sm:px-6 md:grid-cols-[.8fr_1.2fr]">
          <div className="border-b border-[#E8E1DB] py-9 md:border-b-0 md:border-r md:pr-10"><p className="text-[10px] font-bold uppercase text-[#9D772D]">{home.guidance.eyebrow}</p><h2 className="mt-1 font-heading text-[28px] font-semibold leading-tight sm:text-[34px]">{home.guidance.title}</h2><p className="mt-3 max-w-md text-[11px] leading-5 text-[#76675E]">{home.guidance.description}</p></div>
          <div className="grid sm:grid-cols-3 md:pl-10">
            {(home.guidance.items || []).map((item, index) => <Link key={`${item.title}-${index}`} to={item.link || '/contact'} className={`group flex gap-4 py-6 sm:block sm:px-5 sm:py-9 ${index < 2 ? 'border-b border-[#E8E1DB] sm:border-b-0 sm:border-r' : ''}`}><Icon name={['shield', 'leaf', 'hand'][index % 3]} className="h-5 w-5 shrink-0 text-[#9D772D]"/><div><h3 className="font-heading text-lg font-semibold sm:mt-4">{item.title}</h3><p className="mt-1 text-[10px] leading-5 text-[#76675E]">{item.detail}</p><span className="mt-2 inline-flex items-center gap-1 text-[9px] font-bold uppercase text-[#7F5D1E]">Learn more <Icon name="arrow" className="h-3 w-3 transition group-hover:translate-x-0.5"/></span></div></Link>)}
          </div>
        </div>
      </section>}

      {home.cta.isActive && <section className="bg-[#241914] text-white">
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-7 px-5 py-12 sm:px-6 sm:py-14 md:flex-row md:items-center"><div><p className="text-[10px] font-bold uppercase text-[#C9A354]">{home.cta.eyebrow}</p><h2 className="mt-2 max-w-2xl font-heading text-[30px] font-semibold leading-tight sm:text-[40px]">{home.cta.title}</h2><p className="mt-3 text-xs text-[#CFC3BA]">{home.cta.description}</p></div><Link to={home.cta.buttonLink || '/shop'} className="flex h-12 shrink-0 items-center justify-center gap-2 rounded bg-[#B68A32] px-7 text-[10px] font-bold uppercase text-white hover:bg-[#9D772D]">{home.cta.buttonText} <Icon name="arrow" className="h-4 w-4"/></Link></div>
      </section>}
    </div>
  );
}
